import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { MapFeatureLink, MapFeatureLinkSnapshot } from '../models/map-feature-link';
import { CropBox, SHEET_PREVIEW_ZOOM, SheetPreviewCrop, sheetPreviewCrop } from './sheet-preview-crop';
import type { FeatureLinkEntry } from './feature-link-status';
import { PublishedMapLayer } from '../services/project-map-manifest.service';
import { boundsIntersect, InspectionQuery, inspectionTolerance, MapBounds, MapCoordinate, validMapBounds } from './project-map-inspection';

export interface PickLayerEntry { published: PublishedMapLayer; drawOrder: number; }

export interface PickQueryInput {
  projectId: string;
  catalogRevision: number;
  /** The pin in screen pixels. */
  screen: { x: number; y: number };
  /** Screen → [lon, lat]; null when the view cannot project the point. */
  toMap: (x: number, y: number) => MapCoordinate | null;
  /** Map units per pixel (`view.resolution`). */
  resolution: number;
  /** Published layers that are visible and within their zoom range right now. */
  layers: PickLayerEntry[];
}

/** Same query the sites map builds on click (`inspectReferenceClick`), around the pin instead of the click. */
export function buildPickQuery(input: PickQueryInput): InspectionQuery | null {
  if (!input.layers.length) return null;
  const point = input.toMap(input.screen.x, input.screen.y);
  const radius = Math.max(...input.layers.map(entry => inspectionTolerance(entry.published)));
  const corners = [[-radius, -radius], [radius, -radius], [radius, radius], [-radius, radius]]
    .map(([dx, dy]) => input.toMap(input.screen.x + dx, input.screen.y + dy));
  if (!point || corners.some(corner => !corner)) return null;
  const bbox: MapBounds = [Math.min(...corners.map(c => c[0])), Math.min(...corners.map(c => c[1])),
    Math.max(...corners.map(c => c[0])), Math.max(...corners.map(c => c[1]))];
  const nearby = input.layers.filter(entry => validMapBounds(entry.published.bounds) && boundsIntersect(entry.published.bounds, bbox));
  if (!nearby.length) return null;
  return { projectId: input.projectId, catalogRevision: input.catalogRevision, point, bbox, resolution: input.resolution,
    layers: nearby.map(entry => ({ layer: entry.published, drawOrder: entry.drawOrder })) };
}

export interface PickResultState {
  includeGps: boolean;
  latitude: number;
  longitude: number;
  enableLocationSection: boolean;
  locationText: string | null;
  /** The link shown in the picker now: a fresh draft, the existing link, or null. */
  link: MapFeatureLink | null;
  /** The record's link the picker was opened with (card "Change", or a toolbar that knows it). */
  existingLink: MapFeatureLink | null;
  /** The user linked, changed or removed a link in this session. */
  linkTouched: boolean;
  /** The user moved the pin (click or drag) in this session. */
  pinMoved: boolean;
  /** Properties of the feature the user linked in this session (feature_source prefill); returned only with that link. */
  featureProperties?: Record<string, unknown> | null;
}

/** The picker's left pane: the ArcGIS map or a drawing sheet. */
export type PickerTab = 'map' | 'drawing';

/**
 * Spec §12 slice 3: on the Drawing tab, Done waits for a link to send. Only having no link at all blocks it: a record
 * already linked to a map feature stays submittable while the user just looks at the sheets.
 */
export function sheetPickBlocksDone(tab: PickerTab, link: MapFeatureLink | null): boolean {
  return tab === 'drawing' && !link;
}

/** The Location dialog's primary action (Sri's ruling, as on mobile); the code still calls it done() */
export const PICKER_SAVE_LABEL = 'Save';

/** W75 (review I3): what the dialog has changed since it opened, on either tab */
export interface PickerChanges {
  /** A link picked, a sheet point placed or moved, a capture taken or its markup saved, a link removed */
  linkTouched: boolean;
  /** The GPS pin dragged on the Map tab */
  pinMoved: boolean;
  /** Strokes drawn on the sheet in this dialog, held until the record saves */
  heldStrokes: boolean;
  /** The Location text edited */
  locationDirty: boolean;
  /** Include GPS location toggled from where it opened */
  gpsChanged: boolean;
}

export function pickerUnchanged(changes: PickerChanges): boolean {
  return !changes.linkTouched && !changes.pinMoved && !changes.heldStrokes && !changes.locationDirty && !changes.gpsChanged;
}

/**
 * Save (the dialog's primary action, `done()`) on the Drawing tab: grey without a link to send (slice 3), and (W75, as on mobile) until something in the
 * dialog changed, on either tab. The Map tab keeps its own rules.
 */
export function drawingDoneBlocked(tab: PickerTab, link: MapFeatureLink | null, changes: PickerChanges): boolean {
  // Audit 2: no link after a removal (Remove pin) is the change to send, so it does not block
  return (sheetPickBlocksDone(tab, link) && !changes.linkTouched) || (tab === 'drawing' && pickerUnchanged(changes));
}

/**
 * Audit 2: Remove pin on the sheet. With the record's markup on the sheet (`hasMarkup`) the link stays, pinless: the
 * markup shows where (a point is never invented). Without it a sheet-only link goes entirely; a link that also targets a
 * map feature keeps the feature (and its capture) and drops only the sheet target.
 */
export function sheetPinRemoved(link: MapFeatureLink | null | undefined, hasMarkup = false): MapFeatureLink | null {
  if (link?.sheet && hasMarkup) return { ...link, sheet: { ...link.sheet, point: null }, sheet_edited: true };
  // An edit, so the save sends `sheet: null` (an absent key keeps the stored sheet target)
  return link?.map_feature_ref ? { ...link, sheet: null, sheet_edited: true } : null;
}

/** W75: the linked-sheet tile's tooltip and aria-label */
export function sheetTileLabel(hasMarkup: boolean): string {
  return hasMarkup ? 'Edit markup' : 'Mark up';
}

/**
 * W75: the tile's crop of the linked page around the point (square box, the record card's crop), once the page's aspect
 * has been measured on the same image; null before that. With no point (markup only) it fits the strokes, or the page.
 */
export function sheetTileCrop(
  point: { x: number; y: number } | null | undefined, src: string | null, measured: { src: string; aspect: number } | null,
  strokes: CropBox | null = null,
): SheetPreviewCrop | null {
  // Audit 3: fitted to the pin plus the record's strokes (held or saved), as the record card's crop
  return src && measured?.src === src ? sheetPreviewCrop(point ?? null, measured.aspect, 1, SHEET_PREVIEW_ZOOM, strokes) : null;
}

export interface PickResult {
  latitude?: number;
  longitude?: number;
  location?: string | null;
  locationText?: string | null;
  /** Absent = leave the record's link untouched (see `mergePickedLocation`); null clears it. */
  map_feature_link?: MapFeatureLink | null;
  /**
   * The linked feature's properties, for the form's feature_source prefill. Client-side only: never part of
   * map_feature_link or any api payload. Present only when the result carries the link the user just made.
   */
  feature_properties?: Record<string, unknown>;
  /**
   * Launcher only: the user chose "Open existing entry" on a done row (the newest linked form entry). The picker closes
   * with just this; the host opens that entry. Never sent to the api.
   */
  open_entry?: FeatureLinkEntry;
}

/**
 * What the ArcGIS picker publishes on `dailyReportService.selectedLocation` (same keys as the Google picker, plus the link).
 *
 * `map_feature_link` is sent only when the user linked or removed a link, or the picker was opened with an
 * existing link; a plain re-pin omits it so the record's link stays as is (spec §9). An existing link comes
 * back value-for-value unless the pin really moved; then it is re-pinned, its offset cleared and an unmarked
 * snapshot shown as pending (the api recomputes both on save). Excluding GPS sends no coordinates, so the host
 * keeps its pin and its link: the key is omitted unless the user explicitly removed the link.
 */
export function pickResult(state: PickResultState): PickResult {
  const sheetOnly = !!state.link?.sheet && !state.link.map_feature_ref;
  // Ruling W17 Q5: a pick made only on the Drawing tab (the map pin never moved) leaves the record's coordinates alone.
  const unplaced = !state.pinMoved && sheetOnly;
  const result: PickResult = state.includeGps && !unplaced ? { latitude: state.latitude, longitude: state.longitude } : {};
  if (state.enableLocationSection) {
    result.locationText = state.locationText || null;
    result.location = result.locationText;
  }
  if (!state.linkTouched && !state.existingLink) return result;
  if (!state.includeGps) {
    if (state.linkTouched && !state.link) result.map_feature_link = null;
    // A sheet point needs no GPS: the record keeps its pin and gets the sheet link.
    else if (state.linkTouched && sheetOnly) result.map_feature_link = state.link;
  } else if (!state.link) {
    result.map_feature_link = null;
  } else if (sheetOnly) {
    // A sheet-only link has no map pin of its own (the api derives one from a placement).
    result.map_feature_link = state.link;
  } else if (state.pinMoved && !samePin(state.link, state.latitude, state.longitude)) {
    result.map_feature_link = { ...state.link, pin: { latitude: state.latitude, longitude: state.longitude },
      offset_m: null, bearing_deg: null, offset_label: null, snapshot: movedSnapshot(state.link) };
  } else {
    result.map_feature_link = state.link;
  }
  if (state.featureProperties && state.linkTouched && result.map_feature_link) result.feature_properties = state.featureProperties;
  return result;
}

/**
 * A saved link's snapshot shows the old pin once the pin moves: show it as pending (the api re-renders on save) so
 * the old thumbnail and Mark up disappear. A marked-up snapshot is kept as is, because the api keeps it on a moved pin.
 * Drafts have no snapshot yet.
 */
function movedSnapshot(link: MapFeatureLink): MapFeatureLinkSnapshot | null {
  if (!link.uuid || link.snapshot?.has_markup) return link.snapshot;
  return { status: 'pending', error: null, attachment_uuid: null, thumb_url: null, preview_url: null, has_markup: false };
}

function samePin(link: MapFeatureLink, latitude: number, longitude: number): boolean {
  return Number(link.pin?.latitude) === latitude && Number(link.pin?.longitude) === longitude;
}

/**
 * Remembers which record (e.g. a bid-item line) opened a location picker, so its pick lands there even if
 * the selection moved while the picker was opening or open (Review Focus 5).
 */
export class PickTarget {
  private target: string | null = null;

  /** The id a pick should land on: the opener's while its picker is open, else the current selection. */
  resolve(selected: string | null): string | null {
    return this.target || selected;
  }

  /** Call once the picker's dialog ref exists; released when it closes (afterClosed fires after the pick). */
  hold(id: string | null, closed: Observable<unknown>): void {
    this.target = id;
    closed.pipe(take(1)).subscribe(() => {
      if (this.target === id) this.target = null;
    });
  }
}
